import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useMemo } from 'react';

import { EventsListApiResponse } from '../types/events.types';

import { getEventsListService } from 'services/events.service';

import { EVENTS_CACHE_KEYS, FiltersState, USE_MOCK_DATA } from '../constants';
import { getMockEventsList } from '../mocks/events.mock';
import { mapFiltersStateToApiFilters, mapFiltersToParams } from '../utils';

const PAGE_SIZE = 10;

export const useEventsList = (filtersState?: FiltersState) => {
  const apiFilters = useMemo(() => {
    if (!filtersState) return undefined;
    return mapFiltersStateToApiFilters(filtersState);
  }, [filtersState]);

  const params = useMemo(() => mapFiltersToParams(apiFilters), [apiFilters]);

  const queryKey = useMemo(() => [EVENTS_CACHE_KEYS.EVENTS_LIST, params], [params]);

  const queryResult = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) => {
      const page: number = (pageParam as number) ?? 1;

      if (USE_MOCK_DATA) {
        const mockData = getMockEventsList(page, PAGE_SIZE);
        return Promise.resolve({
          data: mockData,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as any,
        } as AxiosResponse<EventsListApiResponse>);
      }

      return getEventsListService(
        {
          ...params,
          page,
          page_size: PAGE_SIZE,
        },
        signal!
      );
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.data.next) return undefined;
      const url = new URL(lastPage.data.next, window.location.origin);
      const page = url.searchParams.get('page');
      return page ? parseInt(page, 10) : undefined;
    },
    initialPageParam: 1,
  });

  const data = queryResult.data as InfiniteData<AxiosResponse<EventsListApiResponse>> | undefined;
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError, error } = queryResult;

  const events = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page: AxiosResponse<EventsListApiResponse>) => page.data.results);
  }, [data]);

  const totalCount = data?.pages[0]?.data.count ?? 0;

  return {
    events,
    totalCount,
    isLoading,
    isError,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
};
